// Big-5 coverage join (VB-C). The Jarts analysis result carries judgments keyed
// by `questionId` but NOT the Big-5 `category` (that lives only on the Django
// Question). So we join each result question back to its category and count, per
// category, how many DISTINCT questions had the focus company surface (an
// `isFocus` subject in the judgment) in the latest DONE run.

import type { Big5Category, Question } from "@/types/vb";
import { BIG5_LABELS } from "@/components/vb";

/** Narrow shape we read off the otherwise-untyped Jarts result blob. */
type ResultSubject = { isFocus?: boolean };
type ResultQuestion = {
  questionId?: string;
  judgment?: { subjects?: ResultSubject[] } | null;
};
type ResultRun = { questions?: ResultQuestion[] };
type AnalysisResult = { runs?: ResultRun[] };

export type CoverageTone = "full" | "partial" | "none";

export interface CategoryCoverage {
  category: Big5Category;
  surfaced: number;
  total: number;
  tone: CoverageTone;
}

/** Fixed display order = the same Marcus "They Ask, You Answer" order the
 *  create wizard uses (Cost, Problems, Head-to-Head, Reviews, Best Of), so the
 *  Big-5 framework reads identically from buyer creation through the verdict. */
export const BIG5_DISPLAY_ORDER: Big5Category[] = [
  "cost",
  "problems",
  "head_to_head",
  "reviews",
  "best_of",
];

function toneFor(surfaced: number, total: number): CoverageTone {
  // 0 surfaced → "none" (covers the 0/0 "no questions in this topic" case too).
  if (surfaced === 0) return "none";
  if (surfaced >= total) return "full";
  return "partial";
}

/**
 * Per-category focus-surfaced coverage.
 * - `total` = the buyer's current questions in that category (denominator).
 * - `surfaced` = distinct question UUIDs in that category whose focus company
 *   surfaced in ANY provider's answer (de-duped via a Set, so multiple runs
 *   don't double-count). Only questions that still exist are counted, so
 *   `surfaced <= total` always holds.
 *
 * `result` is the latest DONE analysis result (or null/undefined while it loads
 * or when no run exists). `questions` is the current question list.
 */
export function computeBig5Coverage(
  result: unknown,
  questions: Question[],
): CategoryCoverage[] {
  const categoryById = new Map<string, Big5Category>(
    questions.map((q) => [q.id, q.category]),
  );

  const totals = new Map<Big5Category, number>();
  for (const q of questions) {
    totals.set(q.category, (totals.get(q.category) ?? 0) + 1);
  }

  const surfacedQ = new Map<Big5Category, Set<string>>();
  const runs = (result as AnalysisResult | null | undefined)?.runs ?? [];
  for (const run of runs) {
    for (const rq of run?.questions ?? []) {
      const qid = rq?.questionId;
      if (!qid) continue;
      const category = categoryById.get(qid);
      if (!category) continue; // question deleted/edited away since the run
      const focusSurfaced = !!rq?.judgment?.subjects?.some((s) => s?.isFocus);
      if (!focusSurfaced) continue;
      if (!surfacedQ.has(category)) surfacedQ.set(category, new Set());
      surfacedQ.get(category)!.add(qid);
    }
  }

  return BIG5_DISPLAY_ORDER.map((category) => {
    const total = totals.get(category) ?? 0;
    const surfaced = surfacedQ.get(category)?.size ?? 0;
    return { category, surfaced, total, tone: toneFor(surfaced, total) };
  });
}

/** True when the current question set has drifted from what the latest run
 *  actually judged - i.e. a question exists now whose id isn't in the run
 *  (added manually or via regenerate since the run). In that state the coverage
 *  numbers undercount (new questions can't have surfaced in an old run), so the
 *  UI should nudge the user to re-run. Only meaningful when a run result exists. */
export function questionsChangedSinceRun(result: unknown, questions: Question[]): boolean {
  const runQuestionIds = new Set<string>();
  const runs = (result as { runs?: { questions?: { questionId?: string }[] }[] } | null | undefined)?.runs ?? [];
  for (const run of runs) {
    for (const q of run?.questions ?? []) {
      if (q?.questionId) runQuestionIds.add(q.questionId);
    }
  }
  if (runQuestionIds.size === 0) return false; // no run to compare against
  return questions.some((q) => !runQuestionIds.has(q.id));
}

export { BIG5_LABELS };
